import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useParams, Link, useLocation, Navigate } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "sonner";
import Home from "@/pages/Home";
import Login from "@/pages/Login";
import Join from "@/pages/Join";
import Dashboard from "@/pages/Dashboard";
import ProDashboard from "@/pages/ProDashboard";
import RequestWizard from "@/pages/RequestWizard";
import RequestSuccess from "@/pages/RequestSuccess";
import AdminDashboard from "@/pages/AdminDashboard";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import ServiceLocationPage from "@/pages/ServiceLocationPage";
import AuthCallback from "@/pages/AuthCallback";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Seo from "@/components/Seo";

const SeoPlaceholder = () => {
  const { state, city, service } = useParams();
  const fmt = (s) => (s || "").split("-").map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
  return (
    <div className="min-h-screen bg-navy text-white flex flex-col items-center justify-center px-6 text-center relative grain" data-testid="seo-placeholder-page">
      <Seo noindex title="Page in preparation | Prime Pro Connect Services" path="/" />
      <p className="uppercase tracking-[0.25em] text-flame text-sm font-heading font-bold mb-6">Prime Pro Connect Services</p>
      <h1 className="font-heading font-black tracking-tighter text-4xl sm:text-5xl lg:text-6xl max-w-3xl">
        {service ? `${fmt(service)} in ${fmt(city)}, ${fmt(state)}` : `${fmt(city)}, ${fmt(state)}`}
      </h1>
      <p className="text-white/60 mt-6 max-w-xl text-base">
        This local page is in preparation. It will be published once verified professionals and real project information are available for this area.
      </p>
      <Link to="/" data-testid="seo-back-home-link" className="mt-10 bg-flame hover:bg-flame-dark text-white font-heading font-bold px-8 py-4 uppercase tracking-wider text-sm transition-colors">
        Back to Home
      </Link>
    </div>
  );
};

const ProtectedRoute = ({ children }) => {
  const { user } = useAuth();
  const location = useLocation();
  if (user === undefined) {
    return (
      <div className="min-h-screen bg-[#F4F5F7] flex items-center justify-center">
        <Seo noindex title="Prime Pro Connect Services" path={location.pathname} />
        <span className="inline-block w-8 h-8 border-2 border-flame border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (!user) return <Navigate to={`/login?next=${encodeURIComponent(location.pathname)}`} replace />;
  return (
    <>
      <Seo noindex title="Prime Pro Connect Services" path={location.pathname} />
      {children}
    </>
  );
};

const GlobalFooter = () => {
  const location = useLocation();
  if (location.pathname === "/" || location.pathname === "/privacy-policy") return null;
  return (
    <footer className="bg-navy py-6" data-testid="global-footer">
      <div className="max-w-6xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-2">
        <p className="text-white/40 text-xs">© 2026 Prime Pro Connect Services</p>
        <Link to="/privacy-policy" data-testid="global-footer-privacy-link" className="text-white/50 hover:text-flame text-xs transition-colors">
          Privacy Policy
        </Link>
      </div>
    </footer>
  );
};

function AppRouter() {
  const location = useLocation(); // read hash from here, not window.location.hash (not reactive)
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/login" element={<><Seo noindex title="Log In | Prime Pro Connect Services" path="/login" /><Login /></>} />
        <Route path="/join" element={<><Seo noindex title="Join | Prime Pro Connect Services" path="/join" /><Join /></>} />
        <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
        <Route path="/pro/dashboard" element={<ProtectedRoute><ProDashboard /></ProtectedRoute>} />
        <Route path="/request/new" element={<ProtectedRoute><RequestWizard /></ProtectedRoute>} />
        <Route path="/request/success" element={<><Seo noindex title="Request Submitted | Prime Pro Connect Services" path="/request/success" /><RequestSuccess /></>} />
        <Route path="/admin" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
        <Route path="/:state/:city" element={<SeoPlaceholder />} />
        <Route path="/:state/:city/:service" element={<SeoPlaceholder />} />
        <Route path="/:slug" element={<ServiceLocationPage />} />
      </Routes>
      <GlobalFooter />
    </>
  );
}

function App() {
  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.09 });
    window.__lenis = lenis;
    let rafId;
    const raf = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    };
    rafId = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);

  return (
    <div className="App">
      <Toaster position="top-center" richColors />
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
